/**
===================================================
Module: frontend.src.components.SortFilterModal.tsx
Description: Modal component for sorting and filtering lists
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/
import React, { useState, useEffect } from "react";
import "../styles/SortFilterModal.css";

export interface SortOption {
  id: string;
  label: string;
  value: string;
}

export interface FilterOption {
  id: string;
  label: string;
  value: string;
  checked: boolean;
}

interface SortFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (sortBy: string, filters: string[]) => void;
  onReset: () => void;
  sortOptions: SortOption[];
  filterOptions: FilterOption[];
  currentSort: string;
  currentFilters: string[];
  title?: string;
}

const SortFilterModal: React.FC<SortFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  sortOptions,
  filterOptions,
  currentSort,
  currentFilters,
  title = "Sort & Filter",
}) => {
  const [selectedSort, setSelectedSort] = useState(currentSort);
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>(
    filterOptions.map((option) => ({
      ...option,
      checked: currentFilters.includes(option.value),
    })),
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedSort(currentSort);
      setSelectedFilters(
        filterOptions.map((option) => ({
          ...option,
          checked: currentFilters.includes(option.value),
        })),
      );
    }
  }, [isOpen, currentSort, currentFilters, filterOptions]);

  const handleSortChange = (value: string) => {
    setSelectedSort(value);
  };

  const handleFilterChange = (filterId: string, checked: boolean) => {
    setSelectedFilters((prev) =>
      prev.map((filter) =>
        filter.id === filterId ? { ...filter, checked } : filter,
      ),
    );
  };

  const handleApply = () => {
    const activeFilters = selectedFilters
      .filter((filter) => filter.checked)
      .map((filter) => filter.value);
    onApply(selectedSort, activeFilters);
    onClose();
  };

  const handleReset = () => {
    onReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="sort-filter-modal-overlay" onClick={onClose}>
      <div
        className="sort-filter-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sort-filter-modal-header">
          <h3 className="sort-filter-modal-title">{title}</h3>
          <button className="sort-filter-modal-close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="sort-filter-modal-body">
          {/* Sort Section */}
          <div className="sort-section">
            <h4 className="section-title">並べ替え</h4>
            <div className="sort-options">
              {sortOptions.map((option) => (
                <label key={option.id} className="sort-option">
                  <input
                    type="radio"
                    name="sort"
                    value={option.value}
                    checked={selectedSort === option.value}
                    onChange={() => handleSortChange(option.value)}
                    className="sort-radio"
                  />
                  <span className="sort-label">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Filter Section */}
          <div className="filter-section">
            <h4 className="section-title">フィルター</h4>
            <div className="filter-options">
              {selectedFilters.map((filter) => (
                <label key={filter.id} className="filter-option">
                  <input
                    type="checkbox"
                    checked={filter.checked}
                    onChange={(e) =>
                      handleFilterChange(filter.id, e.target.checked)
                    }
                    className="filter-checkbox"
                  />
                  <span className="filter-label">{filter.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="sort-filter-modal-actions">
          <button className="btn-reset" onClick={handleReset}>
            リセット
          </button>
          <button className="btn-apply" onClick={handleApply}>
            適用
          </button>
        </div>
      </div>
    </div>
  );
};

export default SortFilterModal;
